import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, Eye, ShieldCheck } from "lucide-react";
import { money, api } from "../lib/api";
import { track } from "../lib/analytics";
import { useCjCart } from "../context/CjCartContext";
import { toast } from "sonner";

const TIER_COLOR = { Excellent: "#1f9d55", Great: "#C9A24B", Good: "#E2661F", Fair: "#8a8a8a" };

const ConfidenceBadge = ({ handle }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    let active = true;
    // light mode: MongoDB-only + cached server-side — no per-card storefront calls
    api.get(`/products/${handle}/confidence`, { params: { light: true } })
      .then(({ data }) => { if (active) setData(data); }).catch(() => {});
    return () => { active = false; };
  }, [handle]);
  if (!data) return null;
  return (
    <span data-testid={`card-confidence-${handle}`}
      className="absolute bottom-3 left-3 flex items-center gap-1 bg-white/95 backdrop-blur px-2 py-1 text-[10px] font-black uppercase tracking-wider shadow-sm"
      style={{ color: TIER_COLOR[data.tier] || "#C9A24B" }}
      title={`TAVEN Confidence: ${data.tier}`}>
      <ShieldCheck className="w-3 h-3" /> {data.score}
    </span>
  );
};

export const ProductCard = ({ product, onQuickView }) => {
  const { add } = useCjCart();
  const navigate = useNavigate();
  const [imgError, setImgError] = useState(false);
  if (!product) return null;
  const to = `/product/${product.handle}`;
  const discount = (product.compareAtPrice && product.price && product.compareAtPrice > product.price)
    ? Math.round((1 - product.price / product.compareAtPrice) * 100) : 0;

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (product.soldOut) { toast.error("This item is sold out"); return; }
    if (add(product, 1, "shopify")) { track("add_to_cart", { item_id: product.handle, item_name: product.title, value: product.price, currency: product.currency }); toast.success("Added to cart", { description: product.title }); }
    else toast.error("This item is currently unavailable");
  };

  return (
    <div data-testid={`product-card-${product.handle}`} className="bg-white border border-black/5 hover:border-black/20 hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition-all duration-300 group flex flex-col h-full">
      <Link to={to} className="block aspect-[4/5] bg-white overflow-hidden relative">
        {product.image && !imgError ? (
          <img src={product.image} alt={product.title} loading="lazy" onError={() => setImgError(true)}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-taven-gray text-muted-foreground text-sm font-display font-bold tracking-widest">TAVEN</div>
        )}
        {product.soldOut ? (
          <span className="absolute top-2 left-2 bg-taven-black text-white text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5">Sold Out</span>
        ) : discount > 0 ? (
          <span className="absolute top-2 left-2 bg-taven-red text-white text-[10px] font-black px-1.5 py-0.5">-{discount}%</span>
        ) : product.onSale && (
          <span className="absolute top-2 left-2 bg-taven-red text-white text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 animate-pulse-badge">Sale</span>
        )}
        {onQuickView && (
          <button data-testid={`quick-view-${product.handle}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); onQuickView(product); }}
            className="absolute top-2 right-2 bg-white/90 backdrop-blur p-1.5 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-taven-black hover:text-white">
            <Eye className="w-3.5 h-3.5" />
          </button>
        )}
        <ConfidenceBadge handle={product.handle} />
      </Link>
      <div className="p-2.5 sm:p-3 flex flex-col flex-grow">
        <Link to={to} className="text-[13px] leading-snug font-semibold text-black line-clamp-2 hover:text-taven-gold transition-colors min-h-[2.4em]">{product.title}</Link>
        <div className="flex items-baseline gap-1.5 mt-2">
          <span className="text-base font-black tracking-tight text-black">{money(product.price, product.currency)}</span>
          {discount > 0 && (
            <span className="text-[11px] text-gray-400 line-through">{money(product.compareAtPrice, product.currency)}</span>
          )}
        </div>
        <div className="flex gap-1.5 mt-2.5 pt-0.5">
          <button data-testid={`add-to-cart-${product.handle}`} onClick={handleAdd} disabled={product.soldOut}
            className="flex-1 bg-taven-black text-white text-[11px] font-bold uppercase tracking-wide py-2 hover:bg-taven-orange transition-colors flex items-center justify-center gap-1 disabled:opacity-40 disabled:hover:bg-taven-black">
            <ShoppingBag className="w-3.5 h-3.5" /> {product.soldOut ? "Sold" : "Add"}
          </button>
          <button data-testid={`shop-now-${product.handle}`} onClick={() => navigate(to)}
            className="flex-1 border border-black text-black text-[11px] font-bold uppercase tracking-wide py-2 hover:bg-black hover:text-white transition-colors">
            Shop
          </button>
        </div>
      </div>
    </div>
  );
};

export const ProductCardSkeleton = () => (
  <div className="bg-white border border-black/5 flex flex-col h-full">
    <div className="aspect-[4/5] skeleton" />
    <div className="p-4 space-y-3">
      <div className="h-3 w-20 skeleton" />
      <div className="h-4 w-full skeleton" />
      <div className="h-5 w-16 skeleton" />
    </div>
  </div>
);

export default ProductCard;
